import { FC, Fragment, useMemo } from "react";
import { Chip, Skeleton } from "@mui/material";
import {
  DynamicFilterItemType,
  ResourceExplorerFilterProvider,
  useGetUsersMeOrganisationsCurrentFiltersByFilterProviderAndFilterTypeValuesQuery as useValuesQuery,
} from "@/services/cloudchipr.api";
import { findAdditionalSelectedValues } from "@/components/pages/common/filters-tree/utils/helpers/findAdditionalSelectedValues";
import { useMenuHook } from "@/utils/hooks/useMenu.hook";
import { FilterTreeDisabledValueAutocompletePopover } from "@/components/pages/common/filters-tree/filters-disabled-view/components/FilterTreeDisabledValueAutocompletePopover";
import { FilterTreeChip } from "@/components/pages/common/filters-tree/filters-disabled-view/components/FilterTreeChip";

interface FilterTreeDisabledValueAutocompleteProps {
  filterType: DynamicFilterItemType;
  filterValue: string[];
  provider: ResourceExplorerFilterProvider;
  filterKey?: string;
}

export const FilterTreeDisabledValueAutocomplete: FC<
  FilterTreeDisabledValueAutocompleteProps
> = ({ filterValue, filterType, provider, filterKey }) => {
  const { anchor, open, openMenu, closeMenu } = useMenuHook();

  const { data, isLoading } = useValuesQuery({
    key: filterKey,
    filterType: filterType,
    filterProvider: provider,
    querySource: "resource_explorer",
  });

  const additionalValues = useMemo(() => {
    return findAdditionalSelectedValues(filterValue, data);
  }, [filterValue, data]);

  const options = useMemo(
    () => (additionalValues ? [...(data ?? []), ...additionalValues] : data),
    [additionalValues, data],
  );

  const optionsMap = useMemo(() => {
    if (!options) return {};

    return options.reduce(
      (acc, option) => {
        acc[option.value] = option.title ?? option.value;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [options]);

  if (isLoading) {
    return (
      <Fragment>
        {filterValue.slice(0, 3).map((value) => (
          <Skeleton key={value} height={24} width={75} />
        ))}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {filterValue.slice(0, 3).map((value) => (
        <FilterTreeChip key={value} title={optionsMap?.[value] ?? value} />
      ))}

      {filterValue.length > 3 && (
        <Chip
          size="small"
          label={`+${filterValue.length - 3}`}
          variant="outlined"
          onClick={openMenu}
          sx={{ cursor: "pointer" }}
        />
      )}

      {open && (
        <FilterTreeDisabledValueAutocompletePopover
          open
          anchor={anchor}
          onClose={closeMenu}
          optionsMap={optionsMap}
          filterValue={filterValue}
        />
      )}
    </Fragment>
  );
};
